import {
  CalculatorsRequest,
  DraftCalculatorInput,
  FuelCurveBasis,
  FuelCurveRow,
  PerformanceInputRow,
  PerformanceKind,
  PerformanceResultRow,
} from "../models/calculators.models";

const ALLOWED_CALCULATORS_USERS = new Set([
  "adrian_moisa@crgl-thirdparty.com",
  "guilhem_servet@cargill.com",
]);
const CAPES_DRAFT_COEFFICIENT = 0.2;
const FIRST_FUEL_CURVE_SPEED = 9;
const LAST_FUEL_CURVE_SPEED = 18;
const FUEL_CURVE_SPEED_STEP = 0.1;

const PERFORMANCE_RESULTS = {
  outsideDoubleAbout: { label: "Outside Double Abt (-0.5 kn & +5% mT)", color: "#FF0000" },
  withinDoubleAbout: { label: "Within Double Abt (-0.5 kn & +5% mT)", color: "#E66C37" },
  withinMinusSpeedAbout: { label: "Within - 0.5 kn Speed Abt", color: "#D9B300" },
  withinPlusSpeedAbout: { label: "Within + 0.5 kn Speed Abt", color: "#88E1B1" },
  betterThanCp: { label: "Better Than CP by + 0.5 kn", color: "#00843D" },
} as const;

export const PERFORMANCE_ROW_LABELS: Record<PerformanceKind, string> = {
  ballastEco: "Ballast ECO",
  ladenEco: "Laden ECO",
  ballastFull: "Ballast FULL",
  ladenFull: "Laden FULL",
};

// Accepts either identity claim because environments expose the account through email or sub.
export const hasCalculatorsAccess = (accessToken?: { email?: string; sub?: string } | null) =>
  [accessToken?.email, accessToken?.sub].some(
    (identity) => typeof identity === "string" && ALLOWED_CALCULATORS_USERS.has(identity.trim().toLowerCase()),
  );

export const calculateNormalizedStw = (draft: DraftCalculatorInput): number | null => {
  const hasSupportedInputs =
    draft.vesselType === "Capes (incl. Newcs)" &&
    draft.condition === "Ballast" &&
    draft.stw > 0 &&
    draft.draft > 0 &&
    draft.normalizationDraft > 0 &&
    draft.speedExponent > 0;

  if (!hasSupportedInputs) {
    return null;
  }

  return roundTo(draft.stw * Math.pow(draft.draft / draft.normalizationDraft, CAPES_DRAFT_COEFFICIENT / draft.speedExponent), 2);
};

export const buildFuelCurve = (basis: FuelCurveBasis, exponent: number): FuelCurveRow[] => {
  if (basis.referenceSpeed <= 0 || exponent <= 0) {
    return [];
  }

  const rowCount = Math.round((LAST_FUEL_CURVE_SPEED - FIRST_FUEL_CURVE_SPEED) / FUEL_CURVE_SPEED_STEP) + 1;

  return Array.from({ length: rowCount }, (_, index) => {
    const speedRef = roundTo(FIRST_FUEL_CURVE_SPEED + index * FUEL_CURVE_SPEED_STEP, 1);
    const unroundedMeConsumption = basis.meConsumption * Math.pow(speedRef / basis.referenceSpeed, exponent);
    const meConsumption = roundTo(unroundedMeConsumption, 1);
    const aeConsumption = roundTo(basis.aeConsumption, 1);

    return {
      speedRef,
      meConsumption,
      aeConsumption,
      totalConsumption: roundTo(unroundedMeConsumption + basis.aeConsumption, 1),
    };
  });
};

export const clearPerformanceInputs = (request: CalculatorsRequest): CalculatorsRequest => ({
  ...request,
  performance: {
    rows: request.performance.rows.map((row) => ({
      ...row,
      expectedSpeed: null,
      expectedConsumption: null,
      cpSpeed: null,
      cpConsumption: null,
    })),
  },
});

// Mirrors the backend's isolated provisional branch until the source semantic-model DAX is exported.
export const calculatePerformanceResult = (row: PerformanceInputRow): PerformanceResultRow => {
  const { expectedSpeed, expectedConsumption, cpSpeed, cpConsumption } = row;

  if (
    expectedSpeed === null ||
    expectedConsumption === null ||
    cpSpeed === null ||
    cpConsumption === null
  ) {
    return { kind: row.kind, label: null, color: null };
  }

  const speedDifference = expectedSpeed - cpSpeed;

  if (speedDifference > 0.5) return { kind: row.kind, ...PERFORMANCE_RESULTS.betterThanCp };
  if (speedDifference >= 0) return { kind: row.kind, ...PERFORMANCE_RESULTS.withinPlusSpeedAbout };
  if (speedDifference >= -0.5) return { kind: row.kind, ...PERFORMANCE_RESULTS.withinMinusSpeedAbout };
  if (expectedConsumption <= cpConsumption * 1.05) {
    return { kind: row.kind, ...PERFORMANCE_RESULTS.withinDoubleAbout };
  }

  return { kind: row.kind, ...PERFORMANCE_RESULTS.outsideDoubleAbout };
};

const roundTo = (value: number, decimals: number) => {
  const factor = Math.pow(10, decimals);

  return Math.round((value + Number.EPSILON) * factor) / factor;
};
